import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { ProfileRepeatOrderButton } from '@eliadmin/core/src/components/profile/profile-repeat-order-button';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type {
  CustomerOrder,
  OrderDocument,
} from '@eliadmin/core/src/api/customers/customers-types';
import type { PropsWithChildren } from 'react';

import { ProfileOrderStatus } from '@eliadmin/core/src/components/profile/profile-order-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DialogTitle } from '@radix-ui/react-dialog';
import { LuX } from 'react-icons/lu';
import { Paragraph } from '../ui/paragraph';
import { HiOutlineArrowDownCircle } from 'react-icons/hi2';
import { downloadFileFromBlob } from '@eliadmin/core/src/api/general/general-api';

type ProfileOrderDialogProps = {
  order: CustomerOrder;
};

export function ProfileOrderDialog(props: PropsWithChildren<ProfileOrderDialogProps>) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const me = useCustomerStore(s => s.me);

  const isPaid = !!props.order.paid;

  const handleDownload = async (document: OrderDocument) => {
    if (!me?.authToken) return;
    downloadFileFromBlob({
      fileId: document.id,
      authToken: me.authToken,
      locale,
      fileName: document.name + '_' + document.number,
    });
  };

  return (
    <Dialog>
      <DialogTrigger>{props.children}</DialogTrigger>
      <DialogContent
        className={cn(
          'w-full max-w-[930px] rounded-[16px] px-4 py-8 sm:max-w-[930px] sm:p-8 md:max-w-[930px] md:p-10 lg:p-12',
          'ea-profile-order-dialog__content',
        )}
      >
        <DialogHeader className="flex w-full flex-row items-start justify-between gap-6">
          <DialogTitle className="w-full text-left text-2xl font-medium tracking-tighter md:text-[28px]">
            {t('text17')} #{props.order.identifier}
          </DialogTitle>
          <DialogClose>
            <LuX size={20} className="max-h-5 min-h-5 min-w-5 max-w-5 text-muted-foreground" />
          </DialogClose>
        </DialogHeader>
        <Separator />
        <div className="flex max-h-[70vh] flex-col items-start justify-start gap-6 overflow-y-auto">
          {props.order.items.map((item, index) => {
            return (
              <>
                <div key={item.id} className="flex w-full items-center justify-between gap-4">
                  <Image
                    src={item.image?.url}
                    alt={resolveImageAlt(item?.image, item?.name)}
                    width={0}
                    height={0}
                    sizes="50vw"
                    className={cn(
                      'max-h-[72px] min-h-[72px] min-w-[72px] max-w-[72px] object-contain md:max-h-[100px] md:min-h-[100px] md:min-w-[100px] md:max-w-[100px]',
                    )}
                  />

                  <div className="flex w-full flex-col items-start justify-start gap-2">
                    <div className="flex w-full flex-col items-start justify-start gap-1">
                      <strong className="text-base font-medium tracking-tighter">
                        {item.name}
                      </strong>
                      <Paragraph>{item.id}</Paragraph>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <p className="text-sm font-medium tracking-tighter sm:text-base">
                        {item.quantity} x {item.price.toFixed(2)} {currency}
                      </p>
                      <p className="text-sm font-medium tracking-tighter sm:text-base">
                        {(item.quantity * item.price).toFixed(2)} {currency}
                      </p>
                    </div>
                  </div>
                </div>
                {index < props.order.items.length - 1 && <Separator />}
              </>
            );
          })}
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-2 rounded-[16px] bg-[#f9fafb] p-6">
          <div className="grid w-full grid-cols-2 gap-6">
            <Paragraph>{t('text394')}:</Paragraph>
            <Paragraph className="text-black">#{props.order.identifier}</Paragraph>
          </div>
          <div className="grid w-full grid-cols-2 gap-6">
            <Paragraph>{t('text393')}:</Paragraph>
            <Paragraph className="text-black">
              {new Date(props.order.created_at).toLocaleString(locale)}
            </Paragraph>
          </div>
          <div className="grid w-full grid-cols-2 gap-6">
            <Paragraph>{t('text26')}:</Paragraph>
            <Paragraph className="text-black">
              {props.order.price_with_vat?.toFixed(2) || props.order.price.toFixed(2)} {currency}
            </Paragraph>
          </div>
          <div className="grid w-full grid-cols-2 gap-6">
            <Paragraph>{t('text245')}:</Paragraph>
            <div className="col-span-1 flex w-full items-start justify-start">
              <ProfileOrderStatus order={props.order} />
            </div>
          </div>
        </div>
        {!props.order.documents.length && <Paragraph>{t('text805')}</Paragraph>}
        {props.order.documents.slice(1, 2).map((document, index) => (
          <Button
            disabled={!isPaid}
            key={index}
            onClick={() => handleDownload(document)}
            className="w-full sm:w-fit"
          >
            <HiOutlineArrowDownCircle size={24} /> {t('text1930')}
          </Button>
        ))}
        <ProfileRepeatOrderButton orderId={props.order.id} className="w-full" />

        <OrderLoyaltyReward reward={props.order.loyalty_reward} refreshOrders />
      </DialogContent>
    </Dialog>
  );
}

function Separator() {
  return <div className="h-[1px] w-full bg-black/10" />;
}
